import React, { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import ContactForm from '../../components/base/ContactForm';
import TestimonialsSlider from '../../components/base/TestimonialsSlider';

const ContactPage: React.FC = () => {
  useEffect(() => {
    // Add Schema.org JSON-LD for contact page
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "name": "Get Your Free Painting Quotes - Durbanville Painters",
      "description": "Connect with Durbanville's best painting contractors. Get multiple quotes and choose the perfect contractor for your project.",
      "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/contact`,
      "mainEntity": {
        "@type": "LocalBusiness",
        "name": "Durbanville Painters",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Durbanville",
          "addressRegion": "Western Cape",
          "addressCountry": "ZA"
        }
      }
    });
    document.head.appendChild(script);

    // Update page title and meta
    document.title = "Get Free Painting Quotes Durbanville | Contact Best Contractors";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Get free quotes from vetted painting contractors in Durbanville. Compare prices and choose the best painter for your interior, exterior, or commercial project.');
    }

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Get Your Free Painting Quotes
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Connect with Durbanville's best painting contractors. Get multiple quotes and choose the perfect contractor for your project.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Request Your Quotes
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Fill out the form below and we'll connect you with up to 3 qualified painting contractors in your area.
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Service
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-shield-check-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Vetted Contractors</h3>
              <p className="text-gray-600">
                All painting contractors in our network are thoroughly screened and verified for quality and reliability.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-money-dollar-circle-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Best Prices</h3>
              <p className="text-gray-600">
                Compare multiple quotes to ensure you get the best value for your painting project.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-[#e9f638] rounded-full flex items-center justify-center mx-auto mb-6">
                <i className="ri-customer-service-2-line text-2xl text-black"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Expert Support</h3>
              <p className="text-gray-600">
                Our team is here to help you find the perfect painting contractor for your specific needs.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Customer Success Stories
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              See what our customers say about the painting contractors we've connected them with
            </p>
          </div>
          <TestimonialsSlider />
        </div>
      </section>

      <Footer />
      
      {/* Elfsight AI Chatbot */}
      <div>
        <script src="https://elfsightcdn.com/platform.js" async></script>
        <div className="elfsight-app-bd0c8f1c-533d-413e-bee7-63e0ccb9fdbf" data-elfsight-app-lazy></div>
      </div>
    </div>
  );
};

export default ContactPage;